import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { useRef, useEffect, useState, useContext } from "react";
import { ThemeContext } from "../../../../theme/ThemeContext";
import AuthContext from "../../../../auth/AuthContext";
import CatalogueService from "../../../../services/CatalogueService";
import { Skeleton } from "primereact/skeleton";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// Custom plugin for rounded corners at bottom
const roundedBarsPlugin = {
  id: 'roundedBars',
  beforeDatasetsDraw(chart) {
    const { ctx, chartArea } = chart;
    
    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
      gradient.addColorStop(0, "#104E66");
      gradient.addColorStop(1, "#C9F0FF");
      
      meta.data.forEach((bar, index) => {
        const { x, y, width, base } = bar;
        const radius = Math.min(8, width / 2); 
        
        ctx.save();
        ctx.fillStyle = gradient;
        
        // Draw rounded rectangle with curves at top and bottom
        ctx.beginPath();
        ctx.moveTo(x - width / 2 + radius, base);
        ctx.lineTo(x + width / 2 - radius, base);
        ctx.quadraticCurveTo(x + width / 2, base, x + width / 2, base - radius);
        ctx.lineTo(x + width / 2, y + radius);
        ctx.quadraticCurveTo(x + width / 2, y, x + width / 2 - radius, y);
        ctx.lineTo(x - width / 2 + radius, y);
        ctx.quadraticCurveTo(x - width / 2, y, x - width / 2, y + radius);
        ctx.lineTo(x - width / 2, base - radius);
        ctx.quadraticCurveTo(x - width / 2, base, x - width / 2 + radius, base);
        ctx.closePath();
        ctx.fill();
        
        ctx.restore();
      });
    });
    
    return false; 
  }
};

export default function MappedIncidentChart() {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const chartRef = useRef(null);
  const authContext = useContext(AuthContext);

  const [allData, setAllData] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState("All Courses");
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [
      {
        label: "Mapped Incidents",
        data: [],
        backgroundColor: "#104E66",
        borderRadius: 0, 
        barPercentage: 0.5, 
        categoryPercentage: 0.6, 
      },
    ],
  });
  const [loading, setLoading] = useState(true);

useEffect(() => {
    const controller = new AbortController();

    async function fetchData() {
      setLoading(true);
      try {
        const response = await CatalogueService.GetIncidentCoursesDashboard(
          authContext,
          controller.signal 
        );

        if (controller.signal.aborted) return; 

        if (response.isSuccess && Array.isArray(response.data)) {
          setAllData(response.data);
          setChartData((prev) => ({
            ...prev,
            labels: response.data.map((item) => item.courseName),
            datasets: [
              {
                ...prev.datasets[0],
                data: response.data.map((item) => item.totalIncidents),
              },
            ],
          }));
        } else {
          console.error("API error:", response.errorMessage || "Unknown error");
        }
      } catch (error) {
        if (error.name === "AbortError") return; 
        console.error("Fetch error:", error);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }

    fetchData();

    return () => controller.abort(); 
  }, [authContext]);

  useEffect(() => {
    if (!loading) {
      if (selectedCourse === "All Courses") {
        setChartData((prev) => ({
          ...prev,
          labels: allData.map((item) => item.courseName),
          datasets: [
            {
              ...prev.datasets[0],
              data: allData.map((item) => item.totalIncidents),
            },
          ],
        }));
      } else {
        const filtered = allData.filter(
          (item) => item.courseName === selectedCourse
        );
        setChartData((prev) => ({
          ...prev,
          labels: filtered.map((item) => item.courseName),
          datasets: [
            {
              ...prev.datasets[0],
              data: filtered.map((item) => item.totalIncidents),
            },
          ],
        }));
      }
    }
  }, [selectedCourse, allData, loading]);

  const getInitials = (str) => {
    if (!str) return "";
    let cleanStr = str.replace(/\(.*?\)/g, "");
    cleanStr = cleanStr.replace(/[-_/]/g, " ");
    cleanStr = cleanStr.replace(/\b(with|and|or|of|the)\b/gi, "");
    cleanStr = cleanStr.replace(/\d+/g, "");
    cleanStr = cleanStr.replace(/\s+/g, " ").trim();
    const words = cleanStr.split(" ").filter(Boolean);
    const initials = words.map((w) => w.charAt(0)).join("");

    return initials.toUpperCase();
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { 
      legend: { display: false },
      roundedBars: true, // Enable custom plugin
    },
    scales: {
      x: {
        ticks: {
          color: isDark ? "#eaeaea" : "#444444",
          callback: (value, index) =>
            getInitials(chartData.labels[index] || ""),
        },
        grid: { display: false },
      },
      y: {
        ticks: { color: isDark ? "#eaeaea" : "#444444" },
        grid: { color: isDark ? "#444444" : "#f1f1f1" },
      },
    },
  };

  return (
    <div
      className="charts d-flex flex-column"
      style={{
        padding: 16,
        minHeight: "337px",
        width: "100%",
        background: isDark ? "#1e1e1e" : "#fff",
        border: isDark ? "1px solid #444" : "1px solid #eee",
        borderRadius: "12px",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        {loading ? (
          <>
            {/* Title skeleton */}
            <Skeleton width="150px" height="30px" />
            {/* Dropdown skeleton */}
            <Skeleton width="100px" height="30px" />
          </>
        ) : (
          <>
            <h6
              style={{
                margin: 0,
                fontSize: 16,
                color: isDark ? "#eaeaea" : "#000",
              }}
            >
              Mapped Incidents
            </h6>
            <select
              value={selectedCourse}
              onChange={(e) => setSelectedCourse(e.target.value)}
              style={{
                border: "1px solid #ddd",
                backgroundColor: isDark ? "#222" : "#fff",
                color: isDark ? "#eaeaea" : "#000",
                borderRadius: 6,
                padding: "4px 8px",
                fontSize: 13,
                maxWidth: "110px",
              }}
              title={selectedCourse}
            >
              <option key="all-courses" value="All Courses">
                All Courses
              </option>
              {allData.map((item) => (
                <option
                  key={item.courseCode || item.courseName}
                  value={item.courseName}
                >
                  {item.courseName}
                </option>
              ))}
            </select>
          </>
        )}
      </div>

      <div style={{ flexGrow: 1, position: "relative" }}>
        {loading ? (
          <div className="h-100 d-flex align-items-end gap-2 px-2 py-3">
            {[...Array(6)].map((_, i) => (
              <Skeleton
                key={i}
                width="100%"
                height={`${70 + i * 30}px`}
                className="rounded-top"
              />
            ))}
          </div>
        ) : (
          <Bar ref={chartRef} data={chartData} options={options} plugins={[roundedBarsPlugin]} />
        )}
      </div>
    </div>
  );
}